import type { L2CChainSnapshot, L2CMetric, L2CMover, L2CPanel, L2CStage } from '../../../types/leadToCash';
import { L2C_MOVERS_PAGE_SIZE } from '../../../types/leadToCash';
import { formatL2CSignedCurrency, formatL2CSignedNumber } from './formatNumbers';

export interface L2CBreakdownStore {
  movers: L2CMover[];
  total_count: number;
}

/**
 * The headline breakdown panel always describes a stage's primary metric
 * (e.g. net cash receipts for the Cash stage). Used so client-side lens_hint
 * regeneration (below) stays consistent with the backend's currency-aware
 * formatting instead of assuming plain numbers.
 */
export function isStagePrimaryMetricCurrency(
  stage: Pick<L2CStage, 'headline_metrics' | 'supporting_metrics' | 'primary_metric_key'>,
): boolean {
  const all: L2CMetric[] = [...(stage.headline_metrics ?? []), ...(stage.supporting_metrics ?? [])];
  const primary = (stage.primary_metric_key ? all.find((m) => m.key === stage.primary_metric_key) : undefined) ?? all[0];
  return primary?.format === 'currency';
}

export function panelHasBreakdownStore(panel: L2CPanel | null | undefined): boolean {
  if (!panel?.breakdowns) return false;
  const driverId = panel.active_driver ?? panel.drivers?.find((d) => d.default)?.id ?? panel.drivers?.[0]?.id;
  return Boolean(driverId && panel.breakdowns[driverId]);
}

export function isPanelBreakdownCurrent(panel: L2CPanel | null | undefined): boolean {
  if (!panel?.what_happened) return false;
  if (panelHasBreakdownStore(panel)) {
    return panel.breakdown_page_size === L2C_MOVERS_PAGE_SIZE;
  }
  return (
    panel.breakdown_page_size === L2C_MOVERS_PAGE_SIZE
    && panel.breakdown_total_count != null
    && panel.movers.length <= L2C_MOVERS_PAGE_SIZE
  );
}

export function paginateStagePanel(
  panel: L2CPanel,
  options: { driverId?: string; page?: number; isCurrency?: boolean; currencySymbol?: string } = {},
): L2CPanel {
  const pageSize = L2C_MOVERS_PAGE_SIZE;
  const page = Math.max(1, options.page ?? panel.breakdown_page ?? 1);
  const driverId = options.driverId
    ?? panel.active_driver
    ?? panel.drivers?.find((d) => d.default)?.id
    ?? panel.drivers?.[0]?.id;

  const store = driverId ? panel.breakdowns?.[driverId] : undefined;
  if (store) {
    const allMovers = store.movers ?? [];
    const total = store.total_count ?? allMovers.length;
    const offset = (page - 1) * pageSize;
    const pageMovers = allMovers.slice(offset, offset + pageSize);
    const driverLabel = panel.drivers?.find((d) => d.id === driverId)?.label;
    const top = allMovers[0];
    const formattedChange = top
      ? options.isCurrency
        ? formatL2CSignedCurrency(top.change, options.currencySymbol ?? '$')
        : formatL2CSignedNumber(top.change)
      : null;
    const lensHint = top && driverLabel
      ? `By ${driverLabel} — largest mover: ${top.dimension} (${formattedChange})`
      : panel.lens_hint;

    return {
      ...panel,
      active_driver: driverId ?? panel.active_driver,
      movers: pageMovers,
      breakdown_total_count: total,
      breakdown_page: page,
      breakdown_page_size: pageSize,
      lens_hint: lensHint ?? panel.lens_hint,
    };
  }

  const total = panel.breakdown_total_count ?? panel.movers.length;
  const offset = (page - 1) * pageSize;
  return {
    ...panel,
    movers: panel.movers.slice(offset, offset + pageSize),
    breakdown_total_count: total,
    breakdown_page: page,
    breakdown_page_size: pageSize,
  };
}

export function normalizeStagePanel(
  panel: L2CPanel | null | undefined,
  options: { isCurrency?: boolean; currencySymbol?: string } = {},
): L2CPanel | null {
  if (!panel?.what_happened) return panel ?? null;
  const needsPaginate = panelHasBreakdownStore(panel)
    || panel.movers.length > L2C_MOVERS_PAGE_SIZE
    || panel.breakdown_page_size !== L2C_MOVERS_PAGE_SIZE;
  if (!needsPaginate) return panel;
  return paginateStagePanel(panel, {
    driverId: panel.active_driver ?? undefined,
    page: panel.breakdown_page ?? 1,
    isCurrency: options.isCurrency,
    currencySymbol: options.currencySymbol,
  });
}

export function normalizeSnapshotPanels(
  snapshot: L2CChainSnapshot,
  currencySymbol = '$',
): L2CChainSnapshot {
  return {
    ...snapshot,
    stages: snapshot.stages.map((stage): L2CStage => ({
      ...stage,
      panel: stage.panel
        ? normalizeStagePanel(stage.panel, { isCurrency: isStagePrimaryMetricCurrency(stage), currencySymbol })
        : stage.panel,
    })),
  };
}

export function updateStagePanel(
  snapshot: L2CChainSnapshot,
  stageId: string,
  panel: L2CPanel,
  currencySymbol = '$',
): L2CChainSnapshot {
  return {
    ...snapshot,
    stages: snapshot.stages.map((stage) =>
      stage.id === stageId
        ? {
            ...stage,
            panel: normalizeStagePanel(panel, {
              isCurrency: isStagePrimaryMetricCurrency(stage),
              currencySymbol,
            }),
          }
        : stage,
    ),
  };
}
